/*
 * Shared IP Location Service
 * --------------------------
 * Fetches https://learn-quran.co/gotolink/get-ip-api-location/ exactly ONCE
 * and caches the result as a Promise. Every caller receives the same response.
 *
 * Usage:
 *   import { getIpLocation } from '@/utils/ipLocation';
 *   const data = await getIpLocation();  // { countryCode: 'ID', ... } | null
 */

/** @type {Promise<Object|null>|null} */
let _promise = null;

/**
 * Returns a cached promise that resolves to the IP-location JSON object,
 * or null on failure / timeout.
 * @param {number} [timeoutMs=1800]
 * @returns {Promise<Object|null>}
 */
export function getIpLocation(timeoutMs = 1800) {
  if (_promise) return _promise;
  _promise = _fetchLocation(timeoutMs);
  return _promise;
}

/**
 * @param {number} timeoutMs
 * @returns {Promise<Object|null>}
 */
async function _fetchLocation(timeoutMs) {
  if (typeof fetch !== 'function') return null;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch('https://learn-quran.co/gotolink/get-ip-api-location/', {
      signal: controller.signal,
    });
    if (!res.ok) return null;
    try {
      return await res.json();
    } catch (_) {
      return null;
    }
  } catch (_) {
    return null; // network error or abort
  } finally {
    clearTimeout(timer);
  }
}
